import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Truck, Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const empty = { name: "", gst_number: "", contact_person: "", email: "", phone: "", address: "" };
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export default function Suppliers() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("suppliers", "edit");
  const confirm = useConfirm();
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Supplier name is required");
    if (form.gst_number.trim() && !GSTIN_RE.test(form.gst_number.trim())) {
      return toast.error("Invalid GSTIN. It must be 15 characters, e.g. 22AAAAA0000A1Z5.");
    }
    setSaving(true);
    const payload = {
      name: form.name, gst_number: form.gst_number, contact_person: form.contact_person,
      email: form.email, phone: form.phone, address: form.address,
    };
    try {
      if (editId) await api.put(`/suppliers/${editId}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editId ? "Supplier updated" : "Supplier added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!(await confirm({ title: "Remove Supplier", description: `Remove ${s.name}? This cannot be undone.`, confirmText: "Remove" }))) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader title="Suppliers" subtitle="Vendor Directory">
        {canEdit && (<Button onClick={openNew} data-testid="add-supplier-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100">
          <Plus size={16} weight="bold" className="mr-1" /> Add Supplier
        </Button>)}
      </PageHeader>

      <div className="p-4 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suppliers-grid">
          {suppliers.length === 0 && (
            <div className="col-span-full bg-white border border-zinc-300 p-12 text-center text-zinc-400">
              <Truck size={32} weight="thin" className="mx-auto mb-3" /> No suppliers yet.
            </div>
          )}
          {suppliers.map((s) => (
            <div key={s.id} className="bg-white border border-zinc-300 p-6" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center"><Truck size={18} weight="bold" /></div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="p-2 text-zinc-500 hover:text-zinc-900"><PencilSimple size={16} weight="bold" /></button>
                  <button onClick={() => remove(s)} data-testid={`delete-supplier-${s.id}`} className="p-2 text-[#DC2626]"><Trash size={16} weight="bold" /></button>
                </div>
              </div>
              <h3 className="font-heading font-bold text-lg mt-3 tracking-tight">{s.name}</h3>
              {s.gst_number && (
                <p className="text-xs text-zinc-500 mt-1">GST: <span className="font-mono text-zinc-800">{s.gst_number}</span></p>
              )}
              <div className="mt-3 space-y-1 text-sm text-zinc-600">
                {s.contact_person && <p>{s.contact_person}</p>}
                {s.phone && <p className="font-mono text-xs">{s.phone}</p>}
                {s.email && <p className="text-xs">{s.email}</p>}
                {s.address && <p className="text-xs text-zinc-400">{s.address}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editId ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Name *</Label>
                <Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">GST Number</Label>
                <Input data-testid="supplier-gst-input" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} maxLength={15} placeholder="22AAAAA0000A1Z5" className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Contact Person</Label>
                <Input data-testid="supplier-contact-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Phone</Label>
                <Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input data-testid="supplier-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Address</Label>
              <Textarea data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="supplier-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
